import { NativeStackNavigationOptions } from '@react-navigation/native-stack';

import { ds } from '@/components/lib/design.system';
import { common } from '@/styles/styles';
import { isAndroid, isIos, isWeb } from '@/utils/constants-platform-specific';

export const MODAL_BORDER_RADIUS = 22;

type ScreenPreset = NativeStackNavigationOptions | ((...args: never[]) => NativeStackNavigationOptions);

export const screenPresets = {
    // utility presets, can be composed with the screen-type presets below
    // needed for the circular pager overlay (FantasyEventsSheets, PlayerProfileSheets)
    circularTransparentSheet: isWeb
        ? { contentStyle: common.flex }
        : {
              animation: 'none',
              presentation: 'transparentModal',
          },
    noAnimation: {
        animation: 'none',
    },
    gestureDisabled: {
        gestureEnabled: false,
    },

    /** Shared `screenOptions` for a nested modal stack (e.g. DepositStack, FantasyPickSlipStack). */
    modalStack: {
        headerShown: false,
        contentStyle: {
            backgroundColor: ds.colorValue.background.neutral.base,
            // flex is needed to fix the scrolling issue on web
            flex: 1,
        },
    },

    // --- new screen-type presets ---

    // plain push, no custom options
    // exists so "page" has an entry here, not because it changes platform behaviour
    page: {},

    // slides up over the current screen, leaving it peeking through at the top
    // sheet() takes up the full height
    // sheet(detents) snaps to the given heights instead (like half height, then full height)
    sheet: (detents?: NativeStackNavigationOptions['sheetAllowedDetents']): NativeStackNavigationOptions =>
        detents
            ? {
                  presentation: 'formSheet',
                  sheetAllowedDetents: detents,
                  sheetCornerRadius: MODAL_BORDER_RADIUS,
                  animation: 'slide_from_bottom',
              }
            : {
                  presentation: 'modal',
                  sheetCornerRadius: MODAL_BORDER_RADIUS,
                  animation: 'slide_from_bottom',
              },

    // covers the whole screen with nothing peeking through behind it, can't be swiped away
    fullScreenCover: {
        presentation: 'fullScreenModal',
        // iOS modals slide up by default, so only Android needs it
        ...(isIos ? {} : { animation: 'slide_from_bottom' }),
    },

    /**
     * like `fullScreenCover` but the screens behind it can still change while it's open
     *
     * use it when the screens behind get replaced (example: Launch gets replaced with Lobby)
     * so closing the cover shows the new ones
     *
     * note: only use it over light screens (Splash, Launch, Login), they keep rendering behind it
     */
    transparentFullScreenCover: {
        ...(isIos && {
            presentation: 'transparentModal',
            animation: 'slide_from_bottom',
            contentStyle: { backgroundColor: ds.colorValue.background.neutral.base },
        }),
        ...(isAndroid && {
            presentation: 'fullScreenModal',
            animation: 'slide_from_bottom',
        }),
    },
} satisfies Record<string, ScreenPreset>;
